import { differenceInMinutes, parseISO } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';

import { gettext } from '@eventbrite/i18n';
import classNames from 'classnames';

import { TimeSlotProps, TimeSlotSignalProps } from '@eventbrite/timed-entry';
import React, { type ReactElement } from 'react';
import {
    FEW_TICKETS_LEFT,
    GOING_FAST,
    POPULAR,
    SOLD_OUT,
} from '../../../constants';
import {
    Discount,
    DiscountType,
    SalesStatus,
    TimeSlot,
    UrgencySignal,
} from '../../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../../utils/rebrand';
import { BoltSvg } from '../assests/BoltSvg';
import { FlameSvg } from '../assests/FlameSvg';
import { PopularRebrandSvg } from '../assests/PopularRebrandSvg';
import { SignalDiscountRebrandSvg } from '../assests/SignalDiscountRebrandSvg';
import { SignalDiscountSvg } from '../assests/SignalDiscountSvg';
import { DayPart, TAB_HEADERS } from '../types';
import defaultStyles from './TimeSlot.module.scss';
import rebrandStyles from './TimeSlot.rebranding.module.scss';

type SignalConfig = {
    condition: boolean;
    icon: ReactElement;
    text: string;
};

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};

const _getStylesModule = (isAppRebrandActive: boolean) => {
    const theme = getStyles(isAppRebrandActive);
    return stylesModules[theme];
};

export function getSessionDayPart(
    startDatetime: string,
    timezone: string,
): keyof DayPart {
    const startTime = utcToZonedTime(startDatetime, timezone);
    const startHour = startTime.getHours();
    if (startHour >= 4 && startHour < 12) return 'Morning';
    if (startHour >= 12 && startHour < 18) return 'Afternoon';
    return 'Night';
}

function isSlotSoldOut(salesStatus: SalesStatus) {
    return salesStatus === SalesStatus.soldOut;
}

function isSlotDisabled(salesStatus: SalesStatus) {
    return [SalesStatus.soldOut, SalesStatus.notOnSale].includes(salesStatus);
}

function getTimeSlotClassName(
    salesStatus: SalesStatus,
    isAppRebrandActive: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    const isSoldOut = isSlotSoldOut(salesStatus);
    const isDisabled = isSlotDisabled(salesStatus);
    return classNames(styles.slot, {
        [styles.soldOutSlot]: isSoldOut,
        [styles.disabledSlot]: isDisabled && !isSoldOut,
    });
}

function getSessionTextClassName(
    salesStatus: SalesStatus,
    isAppRebrandActive: boolean,
) {
    const styles = _getStylesModule(isAppRebrandActive);
    return isSlotDisabled(salesStatus)
        ? styles.disabledSessionText
        : styles.sessionText;
}

function _createSoldOutSignal(
    isAppRebrandActive: boolean,
): TimeSlotSignalProps {
    const styles = _getStylesModule(isAppRebrandActive);
    return {
        textClassName: styles.soldOutSignalText,
        className: styles.soldOutSignal,
        text: SOLD_OUT,
    };
}

function _createDiscountSignal(
    discountValue: number,
    isAppRebrandActive: boolean,
): TimeSlotSignalProps | null {
    const styles = _getStylesModule(isAppRebrandActive);
    // TODO [EB-296073] 👇 handle discount conditions once discounts are integrated
    if (discountValue > 0) {
        return {
            className: styles.discountSignal,
            icon: isAppRebrandActive ? (
                <SignalDiscountRebrandSvg />
            ) : (
                <SignalDiscountSvg />
            ),
        };
    }
    return null;
}

function _getPrimaryUrgencySignal(
    urgencySignals: UrgencySignal,
    isAppRebrandActive: boolean,
): TimeSlotSignalProps | null {
    const styles = _getStylesModule(isAppRebrandActive);
    const signalConfigs: SignalConfig[] = [
        {
            condition: urgencySignals.goingFast,
            icon: <BoltSvg />,
            text: GOING_FAST,
        },
        {
            condition: urgencySignals.fewTickets,
            icon: <FlameSvg />,
            text: FEW_TICKETS_LEFT,
        },
        {
            condition: urgencySignals.popular,
            icon: isAppRebrandActive ? <PopularRebrandSvg /> : <FlameSvg />,
            text: POPULAR,
        },
    ];
    const signal = signalConfigs.find((config) => config.condition);
    return signal
        ? {
              textClassName: styles.primaryUrgencySignalText,
              className: styles.primaryUrgencySignal,
              icon: signal.icon,
              text: signal.text,
          }
        : null;
}

export function getUrgencySignals(
    session: TimeSlot,
    isAppRebrandActive: boolean,
) {
    const { urgencySignals, salesStatus, discount } = session;
    if (isSlotSoldOut(salesStatus)) {
        return [_createSoldOutSignal(isAppRebrandActive)];
    }

    const signals: TimeSlotSignalProps[] = [
        _getPrimaryUrgencySignal(urgencySignals, isAppRebrandActive),
        _createDiscountSignal(discount.discountValue, isAppRebrandActive),
    ].filter(Boolean) as TimeSlotSignalProps[];

    return signals;
}

export const formatDiscountText = (discount: Discount): string =>
    `${discount.discountValue}${
        discount.discountType === DiscountType.percentage ? '%' : ''
    } ${gettext('off')}`;

export function getOverallDiscount(timeSlots: Array<TimeSlot>) {
    const overallDiscount = timeSlots.find(
        (item) => item.discount.discountValue > 0,
    );
    return overallDiscount
        ? formatDiscountText(overallDiscount.discount)
        : undefined;
}

export function getSessionDuration(startUtc: string, endUtc: string) {
    const startDate = parseISO(startUtc);
    const endDate = parseISO(endUtc);
    return differenceInMinutes(endDate, startDate);
}

export function getDurationString(minutes: number) {
    if (minutes < 60) {
        return `${minutes} ${gettext('mins')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    return remainingMinutes === 0
        ? `${hours} ${hours === 1 ? gettext('hr') : gettext('hrs')}`
        : `${(minutes / 60).toFixed(1)} ${gettext('hrs')}`;
}

const getEmptyDayPartMap = (): Record<keyof DayPart, TimeSlotProps[]> => ({
    Morning: [],
    Afternoon: [],
    Night: [],
});

export function getSessionsByDurationAndTimeOfDay({
    timeSlots,
    timezone,
    locale,
    isAppRebrandActive,
}: {
    timeSlots: TimeSlot[];
    timezone: string;
    locale: string;
    isAppRebrandActive: boolean;
}) {
    const sessionsByDuration: Record<number, TimeSlotProps[]> = {};
    const sessionsByDurationAndTimeOfDay: Record<
        number,
        Record<keyof DayPart, TimeSlotProps[]>
    > = {};

    for (const session of timeSlots) {
        const duration = getSessionDuration(
            session.startDatetime,
            session.endDatetime,
        );
        const dayPart = getSessionDayPart(session.startDatetime, timezone);

        const props: TimeSlotProps = {
            id: session.id,
            startDatetime: session.startDatetime,
            urgencySignals: getUrgencySignals(session, isAppRebrandActive),
            timezone,
            locale,
            isDisable: isSlotDisabled(session.salesStatus),
            className: getTimeSlotClassName(
                session.salesStatus,
                isAppRebrandActive,
            ),
            sessionTextClassName: getSessionTextClassName(
                session.salesStatus,
                isAppRebrandActive,
            ),
        };

        sessionsByDuration[duration] ??= [];
        sessionsByDurationAndTimeOfDay[duration] ??= getEmptyDayPartMap();

        sessionsByDuration[duration].push(props);
        sessionsByDurationAndTimeOfDay[duration][dayPart].push(props);
    }

    return {
        sessionsByDuration,
        sessionsByDurationAndTimeOfDay,
    };
}

export const hasTimeSlots = (
    sessionsByDurationAndTimeOfDay: Record<
        string,
        Record<string, TimeSlotProps[]>
    >,
) =>
    Object.values(sessionsByDurationAndTimeOfDay).some((sessions) =>
        Object.values(sessions).some((timeSlots) => timeSlots.length > 0),
    );

export const getSortedSessionsByDuration = (
    sessionsByDuration: Record<string, TimeSlotProps[]>,
): { duration: number; sessions: TimeSlotProps[] }[] =>
    Object.entries(sessionsByDuration)
        .map(([duration, sessions]) => ({
            duration: Number(duration),
            sessions: sessions.sort(
                (a, b) =>
                    new Date(a.startDatetime).getTime() -
                    new Date(b.startDatetime).getTime(),
            ),
        }))
        .filter(({ duration }) => !isNaN(duration) && duration >= 0)
        .sort((a, b) => a.duration - b.duration);

export const getDefaultActiveTab = (
    sessionsByDurationAndTime: Record<string, Record<string, TimeSlotProps[]>>,
): string | null => {
    for (const duration of Object.keys(sessionsByDurationAndTime)) {
        const foundTab = TAB_HEADERS.find(
            (tab) =>
                Object.keys(sessionsByDurationAndTime[duration]).includes(
                    tab,
                ) && sessionsByDurationAndTime[duration][tab]?.length > 0,
        );
        if (foundTab) return foundTab;
    }
    return null;
};

export function getSortedSessionsByDurationAndTimeOfDay(
    sessionsByDurationAndTimeOfDay: Record<
        string,
        Record<string, TimeSlotProps[]>
    >,
) {
    return Object.entries(sessionsByDurationAndTimeOfDay).sort(
        ([a], [b]) => Number(a) - Number(b),
    );
}

export const getFormattedTime = (
    isoString: string,
    locale: string,
    timeZone: string,
): string => {
    if (!isoString || isNaN(Date.parse(isoString))) {
        return '';
    }
    const date = new Date(isoString);
    return date.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone,
    });
};

export function sortSessionsByStartTime(sessions: TimeSlot[]): TimeSlot[] {
    return sessions.sort(
        (a, b) =>
            new Date(a.startDatetime).getTime() -
            new Date(b.startDatetime).getTime(),
    );
}
